import {Component, inject, Input, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {CardComponent} from "../../../libs/card/card.component";
import {CardHeaderComponent} from "../../../libs/card/card-header/card-header.component";
import {CardBodyComponent} from "../../../libs/card/card-body/card-body.component";
import {TranslatePipe} from "@ngx-translate/core";
import {Subscription} from "rxjs";
import {Permissions} from "../../../services/auth/permissions.item";
import {SettingsService} from "../../../services/settings.service";
import {PermissionService} from "../../../services/auth/permission.service";
import {isDefined, isFalse, unsubscribe} from "../../../libs/utils/epic.utils";
import {SettingDto} from "../../../repositories/dto/setting.dto";
import {SettingKey} from "../../../base/setting-key.enum";

import {ButtonComponent} from "../../../libs/button/button.component";
import {UrlService} from "../../../services/url.service";
import {Paths} from "../../../paths";
import {UserService} from "../../../services/user.service";
import {OrgUserDto} from "../../../repositories/dto/org-user.dto";
import {filter, map} from "rxjs/operators";
import {StateService} from "../../../services/state/state.service";
import {AccStateEnum} from "../../../services/state/acc-state.enum";
import {IconComponent} from "../../../libs/icon/icon.component";
import {NgbTooltip} from "@ng-bootstrap/ng-bootstrap";

@Component({
  selector: 'acc-first-steps',
  imports: [
    CardComponent,
    CardHeaderComponent,
    CardBodyComponent,
    TranslatePipe,
    ButtonComponent,
    IconComponent,
    NgbTooltip
  ],
  templateUrl: './first-steps.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './first-steps.component.css'
})
export class FirstStepsComponent implements OnInit, OnDestroy {

  @Input({required: true})
  public state!: AccStateEnum;

  protected perms: Permissions;

  private readonly subs: Subscription[] = [];
  private orgSettings: SettingDto[] = [];
  private assignedOrgs: OrgUserDto[] = [];

  private readonly settingsService: SettingsService = inject(SettingsService);
  private readonly permissionService: PermissionService = inject(PermissionService);
  private readonly urlService: UrlService = inject(UrlService);
  private readonly userService: UserService = inject(UserService);
  private readonly stateService: StateService = inject(StateService);

  constructor() {
    this.perms = this.permissionService.getPerms();
  }

  ngOnInit(): void {
    this.subs.push(
      this.permissionService.accChanged().subscribe((p) => this.perms = p),
      this.settingsService.getOrgSettings().subscribe((s) => this.orgSettings = s),
      this.stateService.getStateDistinct()
        .pipe(filter((s) => s === AccStateEnum.USER))
        .subscribe(() => this.subs.push(this.userService.getAssignedOrgs()
          .pipe(map((os) => os.filter((o) => o.isAllowSwitchToOrg() || o.isInvited())))
          .subscribe((os) => this.assignedOrgs = os)))
    );
  }

  ngOnDestroy(): void {
    unsubscribe(this.subs);
  }

  protected hasFirstSteps(): boolean {
    return this.isFirstStepsOrgAddressMissing()
      || this.isFirstStepsOrgBankAddressMissing()
      || this.isFirstStepsContactMissing()
      || this.isFirstStepsInvoiceDraftMissing()
      || this.isFirstStepsUserRegisterOrg();
  }

  protected isFirstStepsOrgAddressMissing(): boolean {
    return this.state === AccStateEnum.ORG && this.perms.settingsOrg.write && !this.getOrgSettingBooleanValueByKey(SettingKey.ORG_FIRST_STEPS_ADDRESS);
  }

  protected isFirstStepsOrgBankAddressMissing(): boolean {
    return this.state === AccStateEnum.ORG && this.perms.settingsOrg.write && !this.getOrgSettingBooleanValueByKey(SettingKey.ORG_FIRST_STEPS_BANK_ADDRESS);
  }

  protected isFirstStepsContactMissing(): boolean {
    return this.state === AccStateEnum.ORG && this.perms.contact.write && !this.getOrgSettingBooleanValueByKey(SettingKey.ORG_FIRST_STEPS_CONTACT);
  }

  protected isFirstStepsContactDisabled(): boolean {
    return this.isFirstStepsOrgAddressMissing() || this.isFirstStepsOrgBankAddressMissing();
  }

  protected isFirstStepsInvoiceDraftMissing(): boolean {
    return this.state === AccStateEnum.ORG && this.perms.invoice.write && isFalse(this.getOrgSettingBooleanValueByKey(SettingKey.ORG_FIRST_STEPS_INVOICE_DRAFT));
  }

  protected isFirstStepsInvoiceDraftDisabled(): boolean {
    return this.isFirstStepsOrgAddressMissing() || this.isFirstStepsOrgBankAddressMissing() || this.isFirstStepsContactMissing();
  }

  protected isFirstStepsUserRegisterOrg(): boolean {
    return this.state === AccStateEnum.USER && this.assignedOrgs.filter((o) => o.isAllowSwitchToOrg()).length === 0;
  }

  protected isFirstStepsUserInvited(): boolean {
    return this.assignedOrgs.filter((o) => o.isInvited()).length > 0;
  }

  protected gotoOrgSettings(): void {
    this.urlService.navigate([Paths.SETTINGS_ORG]).then();
  }

  protected gotoContactNew(): void {
    this.urlService.navigate([Paths.CONTACT_EDIT, {id: 'new'}]).then();
  }

  protected gotoInvoiceNew(): void {
    this.urlService.navigate([Paths.INVOICE_EDIT, {id: 'new'}]).then();
  }

  protected gotoOrgList(): void {
    this.urlService.navigate([Paths.ORG_LIST]).then();
  }

  protected ignoreFirstStepsOrgBankAddress(): void {
    this.settingsService.updateOrgSetting(SettingKey.ORG_FIRST_STEPS_BANK_ADDRESS, "true").then();
  }

  protected ignoreFirstStepsContact(): void {
    this.settingsService.updateOrgSetting(SettingKey.ORG_FIRST_STEPS_CONTACT, "true").then();
    this.ignoreFirstStepsInvoiceDraft();
  }

  protected ignoreFirstStepsInvoiceDraft(): void {
    this.settingsService.updateOrgSetting(SettingKey.ORG_FIRST_STEPS_INVOICE_DRAFT, "true").then();
  }

  private getOrgSettingBooleanValueByKey(key: SettingKey): boolean {
    const setting = this.orgSettings.find((s) => s.key === key);
    return isDefined(setting) ? setting.isTrue() : false;
  }
}
